import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, Divider, Tab, Tabs, TextField, Typography } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import { Capacitor } from '@capacitor/core';
import { InAppBrowser } from '@capacitor/inappbrowser';
import Header from '../Header';
import { useAuth } from '../../context/AuthContext';
import { apiClient, ApiError } from '../../services/online/apiClient';
import { getErrorMessageKey } from '../../services/online/errorMessages';
import { API_BASE_URL } from '../../config/onlineConfig';

const LOGIN_TAB = 0;
const REGISTER_TAB = 1;

export default function OnlineAuthScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { login, register } = useAuth();

    const [tab, setTab] = useState(LOGIN_TAB);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [providers, setProviders] = useState({ google: false, apple: false });

    const redirectTo = location.state?.from?.pathname ?? '/online';

    // GET /auth/providers reports whether GOOGLE_CLIENT_ID/APPLE_CLIENT_ID
    // are actually set server-side (both are optional env config — see
    // backend/.env.example) — hides these buttons instead of sending the
    // user into a flow that ends in OAUTH_NOT_CONFIGURED.
    useEffect(() => {
        let cancelled = false;
        apiClient
            .get('/auth/providers')
            .then((result) => {
                if (!cancelled) setProviders(result);
            })
            .catch(() => {
                // Backend unreachable/older version without this route — keep both buttons hidden.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleTabChange = (_event, value) => {
        setTab(value);
        setError(null);
    };

    const handleOAuthLogin = async (provider) => {
        setError(null);
        const platform = Capacitor.isNativePlatform() ? 'native' : 'web';
        const url = `${API_BASE_URL}/auth/${provider}?platform=${platform}`;
        if (Capacitor.isNativePlatform()) {
            // OAuth providers refuse to run inside an embedded WebView — the
            // system browser opens it instead, and the backend hands control
            // back to the app via the rondaybaciga://oauth2/callback deep
            // link (see DeepLinkListener.jsx).
            try {
                await InAppBrowser.openInExternalBrowser({ url });
            } catch {
                setError(t(getErrorMessageKey('OAUTH_ERROR')));
            }
        } else {
            window.location.href = url;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setIsSubmitting(true);
        try {
            if (tab === LOGIN_TAB) {
                await login(email, password);
            } else {
                await register(name, email, password);
            }
            navigate(redirectTo, { replace: true });
        } catch (err) {
            const code = err instanceof ApiError ? err.code : undefined;
            setError(t(getErrorMessageKey(code)));
        } finally {
            setIsSubmitting(false);
        }
    };

    const hasOAuthProviders = providers.google || providers.apple;

    return (
        <>
            <Header title={t('online.auth.title')} onBack={() => navigate('/')} showSettings={false} />
            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
                <Card elevation={3}>
                    <Tabs value={tab} onChange={handleTabChange} variant="fullWidth">
                        <Tab label={t('online.auth.login_tab')} />
                        <Tab label={t('online.auth.register_tab')} />
                    </Tabs>
                    <CardContent>
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 2, sm: 3 },
                            alignItems: 'stretch',
                            '@media (orientation: portrait)': { flexDirection: 'column' }
                        }}>
                            {/* Left: email/password form */}
                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                sx={{ flex: '1 1 0%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
                            >
                                {tab === REGISTER_TAB && (
                                    <TextField
                                        label={t('online.auth.name')}
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        fullWidth
                                        autoComplete="name"
                                    />
                                )}
                                <TextField
                                    label={t('online.auth.email')}
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    fullWidth
                                    autoComplete="email"
                                />
                                <TextField
                                    label={t('online.auth.password')}
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    fullWidth
                                    autoComplete={tab === LOGIN_TAB ? 'current-password' : 'new-password'}
                                    inputProps={tab === REGISTER_TAB ? { minLength: 8 } : undefined}
                                    helperText={tab === REGISTER_TAB ? t('online.auth.password_hint') : undefined}
                                />
                                {error && <Alert severity="error">{error}</Alert>}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={isSubmitting}
                                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                                >
                                    {tab === LOGIN_TAB ? t('online.auth.login_button') : t('online.auth.register_button')}
                                </Button>
                            </Box>

                            {hasOAuthProviders && (
                                <>
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{ display: 'none', '@media (orientation: landscape)': { display: 'flex' } }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            {t('online.auth.oauth_divider')}
                                        </Typography>
                                    </Divider>
                                    <Divider sx={{ display: 'none', width: '100%', my: 0.5, '@media (orientation: portrait)': { display: 'block' } }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('online.auth.oauth_divider')}
                                        </Typography>
                                    </Divider>

                                    {/* Right: Google/Apple */}
                                    <Box
                                        sx={{
                                            flex: '1 1 0%',
                                            minWidth: 0,
                                            mt: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: 1.5,
                                            '@media (orientation: portrait)': { flex: 'none', width: '100%' }
                                        }}
                                    >
                                        {providers.google && (
                                            <Button
                                                variant="outlined"
                                                size="large"
                                                fullWidth
                                                startIcon={<GoogleIcon />}
                                                onClick={() => handleOAuthLogin('google')}
                                            >
                                                {t('online.auth.google_button')}
                                            </Button>
                                        )}
                                        {providers.apple && (
                                            <Button
                                                variant="outlined"
                                                size="large"
                                                fullWidth
                                                startIcon={<AppleIcon />}
                                                onClick={() => handleOAuthLogin('apple')}
                                            >
                                                {t('online.auth.apple_button')}
                                            </Button>
                                        )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </>
    );
}
